import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

export function CustomerSearch({ customers, value, onChange, placeholder, style }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!value) { setQuery(''); }
  }, [value]);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = query.length > 0
    ? customers.filter(c => c.code.toLowerCase().includes(query.toLowerCase()) || c.name.toLowerCase().includes(query.toLowerCase()))
    : customers.slice(0, 8);

  const handleSelect = (c) => {
    setQuery(`${c.code} — ${c.name}`);
    setOpen(false);
    onChange(c.id);
  };

  const handleClear = () => {
    setQuery('');
    setOpen(false);
    onChange('');
  };

  return (
    <div ref={ref} style={{position:'relative',...style}}>
      <div style={{position:'relative',display:'flex',alignItems:'center'}}>
        <input
          className="fs"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); if (!e.target.value) onChange(''); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder || 'Search customer code...'}
          style={{paddingRight: value ? 28 : 12, minWidth:180}}
        />
        {value && (
          <button onClick={handleClear} style={{position:'absolute',right:8,background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:16,lineHeight:1}}>×</button>
        )}
      </div>
      {open && filtered.length > 0 && (
        <div style={{position:'absolute',top:'calc(100% + 4px)',left:0,right:0,background:'var(--bg-secondary)',border:'1px solid rgba(15,23,42,0.10)',borderRadius:8,zIndex:50,maxHeight:220,overflowY:'auto',boxShadow:'0 8px 24px rgba(0,0,0,0.5)',minWidth:200}}>
          {filtered.map(c => (
            <div key={c.id} onMouseDown={()=>handleSelect(c)}
              style={{padding:'8px 12px',cursor:'pointer',fontSize:13,color:'var(--text-primary)',borderBottom:'1px solid rgba(15,23,42,0.05)',display:'flex',gap:10,alignItems:'center'}}
              onMouseEnter={e=>e.currentTarget.style.background='var(--border)'}
              onMouseLeave={e=>e.currentTarget.style.background='transparent'}
            >
              <span style={{fontVariantNumeric:"tabular-nums",fontWeight:600,fontWeight:700,color:'var(--accent)',fontSize:12}}>{c.code}</span>
              <span style={{color:'var(--text-secondary)'}}>{c.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function InlineCustomerSearch({ customers, currentId, currentName, onSelect }) {
  const [query, setQuery] = useState(currentName || '');
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 260 });
  const ref = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => { setQuery(currentName || ''); }, [currentName]);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (dropdownRef.current && dropdownRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (!open) return;
    const updatePos = () => {
      if (!inputRef.current) return;
      const r = inputRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 260) });
    };
    updatePos();
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);
    return () => {
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
    };
  }, [open]);

  const filtered = query.length > 0
    ? customers.filter(c => c.code.toLowerCase().includes(query.toLowerCase()) || c.name.toLowerCase().includes(query.toLowerCase()))
    : customers.slice(0, 6);

  return (
    <div ref={ref} style={{position:'relative'}}>
      <input
        ref={inputRef}
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="— Pick customer —"
        style={{
          background: currentId ? 'rgba(22,163,74,0.08)' : 'rgba(217,119,6,0.08)',
          border: `1px solid ${currentId ? 'rgba(22,163,74,0.30)' : 'rgba(217,119,6,0.25)'}`,
          borderRadius:6, padding:'5px 8px', fontSize:12,
          color: currentId ? 'var(--color-success)' : 'var(--color-warning)',
          outline:'none', width:160, cursor:'text'
        }}
      />
      {open && createPortal(
        <div ref={dropdownRef} style={{position:'fixed',top:pos.top,left:pos.left,minWidth:pos.width,background:'var(--bg-secondary)',border:'1px solid rgba(15,23,42,0.10)',borderRadius:8,zIndex:9999,maxHeight:220,overflowY:'auto',boxShadow:'0 8px 24px rgba(0,0,0,0.5)'}}>
          {currentId && (
            <div onMouseDown={()=>{ setQuery(''); setOpen(false); onSelect(''); }}
              style={{padding:'8px 12px',cursor:'pointer',fontSize:13,color:'#b91c1c',borderBottom:'1px solid rgba(15,23,42,0.05)',whiteSpace:'nowrap'}}
              onMouseEnter={e=>e.currentTarget.style.background='var(--border)'}
              onMouseLeave={e=>e.currentTarget.style.background='transparent'}
            >× Unassign customer</div>
          )}
          {filtered.map(c => (
            <div key={c.id} onMouseDown={()=>{ setQuery(`${c.code} — ${c.name}`); setOpen(false); onSelect(c.id); }}
              style={{padding:'8px 12px',cursor:'pointer',fontSize:13,color:'var(--text-primary)',borderBottom:'1px solid rgba(15,23,42,0.05)',display:'flex',gap:10,alignItems:'center',whiteSpace:'nowrap'}}
              onMouseEnter={e=>e.currentTarget.style.background='var(--border)'}
              onMouseLeave={e=>e.currentTarget.style.background='transparent'}
            >
              <span style={{fontVariantNumeric:"tabular-nums",fontWeight:700,color:'var(--accent)',fontSize:12,flexShrink:0}}>{c.code}</span>
              <span style={{color:'var(--text-secondary)',overflow:'hidden',textOverflow:'ellipsis'}}>{c.name}</span>
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}
